// Super Admin — Win-Back Centre (customer recovery automation)
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  RefreshCw, Settings, Send, Eye, BellOff, Ban, Loader2, X, FlaskConical,
  Radio, AlertTriangle, CheckCircle2, Mail,
} from "lucide-react";

const STATUS_COLORS = {
  active: "bg-emerald-100 text-emerald-700", quiet: "bg-zinc-200 text-zinc-600",
  at_risk: "bg-amber-100 text-amber-700", inactive: "bg-red-100 text-red-700",
  win_back: "bg-orange-100 text-orange-700", recovered: "bg-emerald-100 text-emerald-700",
  do_not_contact: "bg-zinc-800 text-zinc-100",
};
const StatusPill = ({ s }) => (
  <span className={`text-[9.5px] font-black uppercase tracking-wider px-2 py-0.5 rounded ${STATUS_COLORS[s] || "bg-zinc-100 text-zinc-600"}`}>{(s || "").replace(/_/g, " ")}</span>
);
const FILTERS = ["all", "quiet", "at_risk", "inactive", "win_back", "recovered", "trial", "paying", "cancelled", "do_not_contact"];

export default function WinBackPanel() {
  const [ov, setOv] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [sim, setSim] = useState(null);

  const load = useCallback(() => {
    api.get("/platform/win-back/overview").then((r) => setOv(r.data)).catch(() => toast.error("Failed to load Win-Back overview"));
    api.get("/platform/win-back/simulation").then((r) => setSim(r.data)).catch(() => {});
  }, []);
  const loadOrgs = useCallback((f = filter, s = q) => {
    api.get("/platform/win-back/orgs", { params: { status: f, q: s || undefined } }).then((r) => setOrgs(r.data)).catch(() => {});
  }, [filter, q]);
  useEffect(() => { load(); loadOrgs(); /* eslint-disable-next-line */ }, []);

  const refresh = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/platform/win-back/refresh");
      toast.success(`Classified ${data.classified} organisations`);
      load(); loadOrgs();
    } catch { toast.error("Refresh failed"); } finally { setBusy(false); }
  };

  const s = ov?.settings;
  return (
    <div data-testid="winback-panel">
      {/* header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display font-black uppercase tracking-tight text-xl">Win-Back Centre</h2>
          <p className="text-xs text-zinc-500 mt-0.5">Identify inactive customers, bring them back to Fieldlink and measure recovered revenue.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {s && (
            <>
              <span className={`inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider px-2.5 py-1.5 rounded ${s.master_enabled ? "bg-emerald-100 text-emerald-700" : "bg-zinc-200 text-zinc-600"}`} data-testid="wb-automation-badge">
                <Radio className="w-3 h-3" /> Automation {s.master_enabled ? "active" : "paused"}
              </span>
              <span className={`inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider px-2.5 py-1.5 rounded ${s.mode === "live" ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"}`} data-testid="wb-mode-badge">
                <FlaskConical className="w-3 h-3" /> {s.mode} mode
              </span>
            </>
          )}
          <button onClick={refresh} disabled={busy} className="h-8 px-3 border border-zinc-300 rounded-md text-[11px] font-bold uppercase inline-flex items-center gap-1.5 hover:border-[#FF5F15]" data-testid="wb-refresh">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Refresh
          </button>
          <button onClick={() => setTemplatesOpen(true)} className="h-8 px-3 border border-zinc-300 rounded-md text-[11px] font-bold uppercase inline-flex items-center gap-1.5 hover:border-[#FF5F15]" data-testid="wb-templates-btn"><Mail className="w-3.5 h-3.5" /> Templates</button>
          <button onClick={() => setSettingsOpen(true)} className="h-8 px-3 bg-[#09090B] text-white rounded-md text-[11px] font-bold uppercase inline-flex items-center gap-1.5" data-testid="wb-settings-btn"><Settings className="w-3.5 h-3.5" /> Settings</button>
        </div>
      </div>

      {/* KPI cards */}
      {ov && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-5" data-testid="wb-kpis">
          {[["At risk", ov.kpis.at_risk, "text-amber-600"], ["Inactive", ov.kpis.inactive, "text-red-600"],
            ["Win-back eligible", ov.kpis.eligible, "text-[#FF5F15]"], ["Recovered this month", ov.kpis.recovered_month, "text-emerald-600"],
            ["Recovered MRR", `$${ov.kpis.recovered_mrr}`, "text-emerald-600"]].map(([l, v, c]) => (
            <div key={l} className="border border-zinc-200 rounded-xl p-4 bg-white">
              <div className={`font-display font-black text-2xl ${c}`}>{v}</div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mt-0.5">{l}</div>
            </div>
          ))}
        </div>
      )}

      {/* test simulation */}
      {sim && s?.mode === "test" && (
        <div className="mt-4 border border-blue-200 bg-blue-50 rounded-xl p-4 flex items-center justify-between flex-wrap gap-3" data-testid="wb-simulation">
          <div>
            <div className="font-display font-black uppercase text-sm text-blue-900">Today's test run</div>
            <div className="text-xs text-blue-800 mt-0.5">
              {sim.count} customer{sim.count !== 1 ? "s" : ""} would receive an email
              {Object.entries(sim.by_status).map(([k, v]) => ` · ${v} ${k.replace(/_/g, " ")}`)}
              {!s.master_enabled && " — automation is paused, nothing sends either way"}
            </div>
          </div>
          {sim.count > 0 && <span className="text-[10px] font-black uppercase text-blue-700">No real emails are sent in test mode</span>}
        </div>
      )}

      {/* funnel */}
      {ov && (
        <div className="mt-4 border border-zinc-200 rounded-xl p-4 bg-white flex flex-wrap gap-x-6 gap-y-2 items-center" data-testid="wb-funnel">
          {[["Identified", ov.funnel.identified], ["Eligible", ov.funnel.eligible], ["Sent (30d)", ov.funnel.sent_30d],
            ["Opened", ov.funnel.opened ?? "Not available"], ["Clicked", ov.funnel.clicked ?? "Not available"], ["Recovered (30d)", ov.funnel.recovered_30d]].map(([l, v], i) => (
            <div key={l} className="flex items-center gap-2">
              {i > 0 && <span className="text-zinc-300">→</span>}
              <div><span className="font-display font-black text-sm">{v}</span> <span className="text-[10px] font-bold uppercase text-zinc-500">{l}</span></div>
            </div>
          ))}
        </div>
      )}

      {/* filters + table */}
      <div className="flex items-center gap-1.5 mt-6 flex-wrap">
        {FILTERS.map((f) => (
          <button key={f} onClick={() => { setFilter(f); loadOrgs(f, q); }} data-testid={`wb-filter-${f}`}
            className={`h-7 px-3 rounded-full text-[10.5px] font-bold uppercase tracking-wide border ${filter === f ? "bg-[#09090B] text-white border-[#09090B]" : "border-zinc-300 text-zinc-500 hover:border-zinc-500"}`}>
            {f.replace(/_/g, " ")}
          </button>
        ))}
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadOrgs(filter, q)} placeholder="Search organisations…"
          className="ml-auto h-8 w-56 border border-zinc-300 rounded-md px-2.5 text-xs outline-none focus:border-[#FF5F15]" data-testid="wb-search" />
      </div>
      <div className="mt-3 border border-zinc-200 rounded-xl overflow-x-auto bg-white">
        <table className="w-full text-xs" data-testid="wb-table">
          <thead className="bg-zinc-50 text-[9.5px] font-black uppercase tracking-widest text-zinc-500">
            <tr>{["Organisation", "Status", "Last active", "Days", "Plan", "Users", "Previous usage", "Emails", "Next action", "Automation"].map((h) => <th key={h} className="text-left px-3 py-2.5">{h}</th>)}</tr>
          </thead>
          <tbody>
            {orgs.map((o) => (
              <tr key={o.org_id} onClick={() => setDetail(o.org_id)} className="border-t border-zinc-100 hover:bg-orange-50/40 cursor-pointer" data-testid={`wb-row-${o.org_id}`}>
                <td className="px-3 py-2.5 font-bold">{o.org_name}{o.high_value && <span className="ml-1.5 text-[9px] font-black text-amber-600 uppercase" title="High-value customer — consider manual contact">⚠ HV</span>}</td>
                <td className="px-3 py-2.5"><StatusPill s={o.status} /></td>
                <td className="px-3 py-2.5 text-zinc-500">{o.last_meaningful_activity ? o.last_meaningful_activity.slice(0, 10) : "Never"}</td>
                <td className="px-3 py-2.5 font-bold">{o.days_inactive ?? "—"}</td>
                <td className="px-3 py-2.5 capitalize">{o.plan || "—"} <span className="text-zinc-400">({o.plan_status || "?"})</span></td>
                <td className="px-3 py-2.5">{o.users_count}</td>
                <td className="px-3 py-2.5 text-zinc-500 max-w-[180px] truncate">{(o.used_features || []).slice(0, 3).join(" • ") || "Never used"}</td>
                <td className="px-3 py-2.5">{o.emails_sent || 0}</td>
                <td className="px-3 py-2.5 text-zinc-600 max-w-[200px] truncate">{o.next_action}</td>
                <td className="px-3 py-2.5">
                  {(o.do_not_contact || {}).on ? <span className="text-[9px] font-black uppercase text-red-600">DNC</span>
                    : o.snooze_until && o.snooze_until > new Date().toISOString() ? <span className="text-[9px] font-black uppercase text-amber-600">Snoozed</span>
                    : <span className={`text-[9px] font-black uppercase ${o.automation_enabled ? "text-emerald-600" : "text-zinc-400"}`}>{o.automation_enabled ? "On" : "Off"}</span>}
                </td>
              </tr>
            ))}
            {orgs.length === 0 && <tr><td colSpan={10} className="px-3 py-8 text-center text-zinc-400">No organisations match — hit Refresh to classify.</td></tr>}
          </tbody>
        </table>
      </div>

      {detail && <OrgDrawer orgId={detail} mode={s?.mode} onClose={() => { setDetail(null); loadOrgs(); load(); }} />}
      {settingsOpen && <SettingsDialog onClose={() => { setSettingsOpen(false); load(); }} />}
      {templatesOpen && <TemplatesDialog onClose={() => setTemplatesOpen(false)} />}
    </div>
  );
}

function OrgDrawer({ orgId, mode, onClose }) {
  const [d, setD] = useState(null);
  const [preview, setPreview] = useState(null);
  const [dncReason, setDncReason] = useState("");
  const load = useCallback(() => api.get(`/platform/win-back/orgs/${orgId}`).then((r) => setD(r.data)).catch(() => toast.error("Failed to load")), [orgId]);
  useEffect(() => { load(); }, [load]);

  const patch = async (body, msg) => {
    try { await api.patch(`/platform/win-back/orgs/${orgId}/automation`, body); toast.success(msg); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const doPreview = async (template) => {
    try { const { data } = await api.get(`/platform/win-back/orgs/${orgId}/preview`, { params: { template } }); setPreview(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
  };
  const sendNow = async () => {
    if (!preview) return;
    try {
      const { data } = await api.post(`/platform/win-back/orgs/${orgId}/send`, { template: preview.template, confirm: true });
      toast.success(`Sent to ${data.to}`); setPreview(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex justify-end" onClick={onClose} data-testid="wb-drawer">
      <div className="bg-white w-full max-w-xl h-full overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="float-right text-zinc-400 hover:text-zinc-900" data-testid="wb-drawer-close"><X className="w-5 h-5" /></button>
        {!d ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#FF5F15]" /></div> : (
          <>
            <h3 className="font-display font-black uppercase text-xl">{d.org_name}</h3>
            <div className="flex items-center gap-2 mt-1.5"><StatusPill s={d.status} />
              <span className="text-xs font-bold text-zinc-500">{d.days_inactive ?? "—"} days inactive</span>
              {d.high_value && <span className="text-[9.5px] font-black uppercase text-amber-600">⚠ High-value — consider manual contact</span>}
            </div>

            <div className="grid grid-cols-2 gap-2.5 mt-5 text-xs">
              {[["Plan", `${d.plan || "—"} (${d.plan_status || "?"})`], ["Customer since", (d.org_created_at || "").slice(0, 10) || "—"],
                ["Users", d.users_count], ["Last meaningful activity", (d.last_meaningful_activity || "Never").slice(0, 10)],
                ["Monthly value", d.monthly_price ? `$${d.monthly_price}` : "—"], ["Emails sent", d.emails_sent || 0]].map(([k, v]) => (
                <div key={k} className="border border-zinc-200 rounded-lg p-2.5"><div className="text-[9px] font-black uppercase tracking-widest text-zinc-400">{k}</div><div className="font-bold mt-0.5">{v}</div></div>
              ))}
            </div>

            <div className="mt-5">
              <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Previous Fieldlink usage</div>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {Object.entries(d.usage || {}).map(([k, v]) => (
                  <div key={k} className="border border-zinc-200 rounded-lg p-2 text-center"><div className="font-display font-black text-base">{v}</div><div className="text-[9px] font-bold uppercase text-zinc-500">{k}</div></div>
                ))}
                {Object.keys(d.usage || {}).length === 0 && <div className="col-span-3 text-xs text-zinc-400">Never meaningfully used Fieldlink.</div>}
              </div>
              {(d.never_used_features || []).length > 0 && (
                <div className="mt-2.5 text-xs"><span className="font-black uppercase text-[9.5px] text-zinc-400">Never used:</span> <span className="text-zinc-600">{d.never_used_features.join(", ")}</span></div>
              )}
            </div>

            {(d.new_since_left || []).length > 0 && (
              <div className="mt-5" data-testid="wb-new-since-left">
                <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500">New since they left</div>
                <ul className="mt-2 space-y-1">
                  {d.new_since_left.map((f) => <li key={f.title} className="text-xs flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-[#FF5F15] shrink-0" /> <b>{f.title}</b> <span className="text-zinc-400">{f.date}</span></li>)}
                </ul>
              </div>
            )}

            {/* automation controls */}
            <div className="mt-6 border border-zinc-200 rounded-xl p-4">
              <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Automation</div>
              <div className="text-xs mt-1.5">{d.eligibility?.eligible ? <span className="text-emerald-600 font-bold">Eligible: {d.eligibility.reason}</span> : <span className="text-zinc-500">{d.eligibility?.reason}</span>}</div>
              {d.recipient && <div className="text-xs text-zinc-500 mt-1">Contact: <b>{d.recipient.email}</b></div>}
              {d.snooze_until && d.snooze_until > new Date().toISOString() && <div className="text-xs text-amber-600 font-bold mt-1">Automation paused until {d.snooze_until.slice(0, 10)}</div>}
              {(d.do_not_contact || {}).on && <div className="text-xs text-red-600 font-bold mt-1">DO NOT CONTACT — {d.do_not_contact.reason || "no reason recorded"} (by {d.do_not_contact.by})</div>}
              <div className="flex flex-wrap gap-2 mt-3">
                <button onClick={() => doPreview(d.eligibility?.template || "win_back")} className="h-8 px-3 bg-[#FF5F15] text-white rounded-md text-[10.5px] font-black uppercase inline-flex items-center gap-1.5" data-testid="wb-send-now"><Send className="w-3 h-3" /> Send win-back email</button>
                <button onClick={() => patch({ enabled: !d.automation_enabled }, d.automation_enabled ? "Automation off for this customer" : "Automation on")} className="h-8 px-3 border border-zinc-300 rounded-md text-[10.5px] font-black uppercase" data-testid="wb-toggle-org-auto">{d.automation_enabled ? "Turn off for this customer" : "Turn on automation"}</button>
                <select onChange={(e) => e.target.value && patch({ snooze_days: Number(e.target.value) }, "Snoozed")} defaultValue="" className="h-8 border border-zinc-300 rounded-md px-2 text-[10.5px] font-bold uppercase" data-testid="wb-snooze">
                  <option value="" disabled>Snooze…</option>
                  {[7, 14, 30, 60].map((n) => <option key={n} value={n}>{n} days</option>)}
                </select>
                {(d.do_not_contact || {}).on
                  ? <button onClick={() => patch({ do_not_contact: { on: false } }, "DNC cleared")} className="h-8 px-3 border border-zinc-300 rounded-md text-[10.5px] font-black uppercase">Clear DNC</button>
                  : <span className="inline-flex items-center gap-1.5">
                      <input value={dncReason} onChange={(e) => setDncReason(e.target.value)} placeholder="DNC reason" className="h-8 w-28 border border-zinc-300 rounded-md px-2 text-[10.5px]" data-testid="wb-dnc-reason" />
                      <button onClick={() => patch({ do_not_contact: { on: true, reason: dncReason } }, "Marked Do Not Contact")} className="h-8 px-3 bg-red-600 text-white rounded-md text-[10.5px] font-black uppercase inline-flex items-center gap-1" data-testid="wb-dnc-btn"><Ban className="w-3 h-3" /> Do not contact</button>
                    </span>}
              </div>
              {mode === "test" && <div className="text-[10px] font-bold text-blue-600 mt-2 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> TEST mode — sends are blocked until you switch to LIVE.</div>}
            </div>

            {/* contact history */}
            <div className="mt-5">
              <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Contact & automation history</div>
              <div className="mt-2 space-y-1.5 max-h-56 overflow-y-auto">
                {(d.events || []).map((e) => (
                  <div key={e.id} className="text-xs flex gap-2"><span className="text-zinc-400 shrink-0 w-20">{e.at.slice(5, 16).replace("T", " ")}</span><span><b className="uppercase text-[9.5px] tracking-wide">{e.kind.replace(/_/g, " ")}</b> — {e.detail}</span></div>
                ))}
                {(d.events || []).length === 0 && <div className="text-xs text-zinc-400">No win-back history yet.</div>}
              </div>
            </div>
          </>
        )}
        {preview && (
          <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
            <div className="bg-white rounded-xl max-w-lg w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="wb-preview-modal">
              <div className="text-[10px] font-black uppercase tracking-widest text-zinc-400">Preview — {preview.template}</div>
              <div className="text-xs mt-2">To: <b>{preview.recipient?.email}</b></div>
              <div className="font-bold text-sm mt-1">{preview.subject}</div>
              <pre className="text-xs whitespace-pre-wrap bg-zinc-50 border border-zinc-200 rounded-lg p-3 mt-2 max-h-64 overflow-y-auto font-sans">{preview.body}</pre>
              <div className="flex justify-end gap-2 mt-3">
                <button onClick={() => setPreview(null)} className="h-9 px-4 border border-zinc-300 rounded-md text-[11px] font-black uppercase" data-testid="wb-preview-cancel">Cancel</button>
                <button onClick={sendNow} className="h-9 px-4 bg-[#FF5F15] text-white rounded-md text-[11px] font-black uppercase" data-testid="wb-preview-send">Confirm & send</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SettingsDialog({ onClose }) {
  const [s, setS] = useState(null);
  const [confirmLive, setConfirmLive] = useState(false);
  useEffect(() => { api.get("/platform/win-back/settings").then((r) => setS(r.data)); }, []);
  const patch = async (body, msg) => {
    try { const { data } = await api.patch("/platform/win-back/settings", body); setS(data); toast.success(msg); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  if (!s) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-lg w-full p-6 my-8" onClick={(e) => e.stopPropagation()} data-testid="wb-settings-modal">
        <h3 className="font-display font-black uppercase text-lg">Win-Back settings</h3>

        <div className="flex items-center justify-between mt-4 border border-zinc-200 rounded-lg p-3">
          <div><div className="font-bold text-sm">Win-back automation</div><div className="text-[11px] text-zinc-500">Automatically identify inactive organisations and send configured recovery emails.</div></div>
          <button onClick={() => patch({ master_enabled: !s.master_enabled }, s.master_enabled ? "Automation paused" : "Automation enabled")}
            className={`h-8 px-4 rounded-md text-[10.5px] font-black uppercase ${s.master_enabled ? "bg-emerald-600 text-white" : "bg-zinc-200 text-zinc-600"}`} data-testid="wb-master-toggle">
            {s.master_enabled ? "ON" : "OFF"}
          </button>
        </div>

        <div className="flex items-center justify-between mt-3 border border-zinc-200 rounded-lg p-3">
          <div><div className="font-bold text-sm">Mode</div><div className="text-[11px] text-zinc-500">TEST classifies and previews but sends zero real emails.</div></div>
          {s.mode === "test" ? (
            confirmLive ? (
              <span className="flex items-center gap-2">
                <span className="text-[10px] font-bold text-red-600">Enable LIVE emails?</span>
                <button onClick={() => { patch({ mode: "live", confirm_live: true }, "LIVE mode enabled"); setConfirmLive(false); }} className="h-8 px-3 bg-red-600 text-white rounded-md text-[10.5px] font-black uppercase" data-testid="wb-confirm-live">Enable live</button>
                <button onClick={() => setConfirmLive(false)} className="h-8 px-3 border border-zinc-300 rounded-md text-[10.5px] font-black uppercase">Cancel</button>
              </span>
            ) : <button onClick={() => setConfirmLive(true)} className="h-8 px-4 bg-blue-100 text-blue-700 rounded-md text-[10.5px] font-black uppercase" data-testid="wb-mode-toggle">TEST → switch to live</button>
          ) : <button onClick={() => patch({ mode: "test" }, "Back to TEST mode")} className="h-8 px-4 bg-red-100 text-red-700 rounded-md text-[10.5px] font-black uppercase" data-testid="wb-mode-toggle">LIVE → back to test</button>}
        </div>

        <div className="mt-4">
          <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Stage thresholds (days without meaningful activity)</div>
          <div className="grid grid-cols-5 gap-2 mt-2">
            {Object.entries(s.thresholds).map(([k, v]) => (
              <div key={k}><label className="text-[9px] font-bold uppercase text-zinc-400">{k.replace(/_/g, " ")}</label>
                <input type="number" defaultValue={v} onBlur={(e) => Number(e.target.value) !== v && patch({ thresholds: { [k]: Number(e.target.value) } }, "Threshold updated")}
                  className="w-full h-8 border border-zinc-300 rounded-md px-2 text-xs" data-testid={`wb-th-${k}`} /></div>
            ))}
          </div>
        </div>

        <div className="mt-4">
          <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Automation stages</div>
          {Object.entries(s.stages).map(([k, v]) => (
            <div key={k} className="flex items-center justify-between border-b border-zinc-100 py-2">
              <div className="text-xs"><b className="uppercase">{k.replace(/_/g, " ")}</b> <span className="text-zinc-500">— {v.label}</span></div>
              <button onClick={() => patch({ stages: { [k]: { enabled: !v.enabled } } }, `Stage ${v.enabled ? "disabled" : "enabled"}`)}
                className={`h-6 px-3 rounded text-[9.5px] font-black uppercase ${v.enabled ? "bg-emerald-100 text-emerald-700" : "bg-zinc-200 text-zinc-500"}`} data-testid={`wb-stage-${k}`}>{v.enabled ? "On" : "Off"}</button>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-3 mt-4">
          <div><label className="text-[9px] font-bold uppercase text-zinc-400">Min days between emails</label>
            <input type="number" defaultValue={s.min_days_between_emails} onBlur={(e) => patch({ min_days_between_emails: Number(e.target.value) }, "Updated")} className="w-full h-8 border border-zinc-300 rounded-md px-2 text-xs" /></div>
          <div><label className="text-[9px] font-bold uppercase text-zinc-400">Max emails per organisation</label>
            <input type="number" defaultValue={s.max_emails_per_org} onBlur={(e) => patch({ max_emails_per_org: Number(e.target.value) }, "Updated")} className="w-full h-8 border border-zinc-300 rounded-md px-2 text-xs" /></div>
        </div>

        {s.master_enabled && (
          <button onClick={() => patch({ master_enabled: false }, "ALL win-back emails paused")} className="mt-5 w-full h-10 bg-red-600 text-white rounded-md text-[11px] font-black uppercase inline-flex items-center justify-center gap-2" data-testid="wb-emergency-stop">
            <BellOff className="w-4 h-4" /> Pause all win-back emails
          </button>
        )}
        <button onClick={onClose} className="mt-3 w-full h-9 border border-zinc-300 rounded-md text-[11px] font-black uppercase" data-testid="wb-settings-close">Close</button>
      </div>
    </div>
  );
}

function TemplatesDialog({ onClose }) {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  useEffect(() => { api.get("/platform/win-back/templates").then((r) => setItems(r.data)); }, []);
  const save = async () => {
    try {
      await api.patch(`/platform/win-back/templates/${edit.key}`, { subject: edit.subject, body: edit.body });
      toast.success("Template saved"); setEdit(null);
      api.get("/platform/win-back/templates").then((r) => setItems(r.data));
    } catch { toast.error("Save failed"); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-2xl w-full p-6 my-8" onClick={(e) => e.stopPropagation()} data-testid="wb-templates-modal">
        <h3 className="font-display font-black uppercase text-lg">Win-back email templates</h3>
        <p className="text-[11px] text-zinc-500 mt-0.5">Placeholders: {"{{first_name}} {{org_name}} {{relevant_features}}"}</p>
        {!edit ? (
          <div className="mt-4 space-y-2">
            {items.map((t) => (
              <div key={t.key} className="flex items-center justify-between border border-zinc-200 rounded-lg p-3">
                <div><div className="font-bold text-sm">{t.name}</div><div className="text-[11px] text-zinc-500">{t.subject}</div></div>
                <button onClick={() => setEdit(t)} className="h-8 px-3 border border-zinc-300 rounded-md text-[10.5px] font-black uppercase hover:border-[#FF5F15]" data-testid={`wb-tpl-edit-${t.key}`}>Edit</button>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-4">
            <div className="font-bold text-sm">{edit.name}</div>
            <label className="text-[9px] font-bold uppercase text-zinc-400 mt-3 block">Subject</label>
            <input value={edit.subject} onChange={(e) => setEdit({ ...edit, subject: e.target.value })} className="w-full h-9 border border-zinc-300 rounded-md px-2.5 text-sm" data-testid="wb-tpl-subject" />
            <label className="text-[9px] font-bold uppercase text-zinc-400 mt-3 block">Body</label>
            <textarea value={edit.body} onChange={(e) => setEdit({ ...edit, body: e.target.value })} rows={12} className="w-full border border-zinc-300 rounded-md px-2.5 py-2 text-sm font-mono" data-testid="wb-tpl-body" />
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => setEdit(null)} className="h-9 px-4 border border-zinc-300 rounded-md text-[11px] font-black uppercase">Back</button>
              <button onClick={save} className="h-9 px-4 bg-[#FF5F15] text-white rounded-md text-[11px] font-black uppercase" data-testid="wb-tpl-save">Save</button>
            </div>
          </div>
        )}
        {!edit && <button onClick={onClose} className="mt-4 w-full h-9 border border-zinc-300 rounded-md text-[11px] font-black uppercase">Close</button>}
      </div>
    </div>
  );
}
